import { DATA_SOURCES, isCovered } from '@/config/constants';
import {
    TabButtonBetaBadge,
    TabButtonDisabled,
    TabButtonLink,
    TabList,
} from 'components/DataSourceTab';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/ui/design-system/components/ui/tooltip';
import Stack from 'components/Stack';
import useCountryParam from 'utils/hooks/useCountryParam';
import { useCurrentCountry } from 'utils/hooks';
import useDataSourceToggle from 'utils/hooks/useDataSourceToggle';
import { views } from '@/copy';

/**
 * DataSourceTabsByCoverage - the data-source tab row for an entity.
 *
 * Coverage decides how a tab renders, not whether it is there: a source that
 * does not cover this market keeps its place, inert and explained by a tooltip,
 * and arriving on one is not redirected away. That holds when no source covers
 * the market either — every tab inert still says what was tried.
 *
 * @param {Object} props
 * @param {string} props.entityId - The ID of the entity
 * @param {string} props.parentId - Optional parent ID for nested routes
 * @param {string} props.routePrefix - The route prefix (e.g., '/categoryFive', '/brands')
 * @param {Object} props.hierarchyHint - The hierarchy hint object containing cat1
 * @param {Object} props.hierarchyHintBySource - Per-source hierarchy hints, so each
 *   tab is judged on its own taxonomy; falls back to hierarchyHint per source
 * @param {boolean} props.customMode - Use custom mode for explore pages (default: false)
 * @param {string} props.customOverviewTo - Custom Overview tab destination
 * @param {string} props.customGoogleTo - Custom Google tab destination
 * @param {string} props.customTikTokTo - Custom TikTok tab destination
 * @param {string} props.customInstagramTo - Custom Instagram tab destination
 * @param {Array} props.unsupportedTabs - Filter out unsupported tabs
 * @returns {JSX.Element}
 */
function DataSourceTabsByCoverage({
    entityId,
    parentId,
    routePrefix,
    hierarchyHint,
    hierarchyHintBySource,
    customMode = false,
    customOverviewTo,
    customGoogleTo,
    customTikTokTo,
    customInstagramTo,
    unsupportedTabs = [],
}) {
    const { mutateToWithCountryParam } = useCountryParam();
    const { labels, mode, modes } = useDataSourceToggle();
    const country = useCurrentCountry();

    // Build route path based on whether there's a parent ID
    const buildRoute = (path = '') => {
        if (customMode) return null; // Custom routes will be provided directly
        const basePath = parentId
            ? `${routePrefix}/${parentId}/${entityId}`
            : `${routePrefix}/${entityId}`;
        return path ? `${basePath}/${path}` : basePath;
    };

    // Each tab is judged on its own taxonomy's cat1 where the caller can supply
    // one. Sources run their ETLs separately, so any of them can carry its own
    // version of the hierarchy and put an id under a different industry; a
    // shared cat1 would then offer or hide a tab on the strength of a taxonomy
    // the tab does not use.
    const coverageFor = (dataSource) => {
        const cat1 =
            hierarchyHintBySource?.[dataSource]?.cat1 ?? hierarchyHint?.cat1;
        if (!(country && cat1)) return true;
        return isCovered({ geo: country?.text_id, dataSource, cat1 });
    };

    const hasOverviewCoverage = coverageFor(DATA_SOURCES.POPULARITY);
    const hasTikTokCoverage = coverageFor(DATA_SOURCES.TIKTOK);
    const hasGoogleCoverage = coverageFor(DATA_SOURCES.GOOGLE);
    const hasInstagramCoverage = coverageFor(DATA_SOURCES.INSTAGRAM);

    const toUrl = (pathOrCustom) =>
        pathOrCustom ? mutateToWithCountryParam(pathOrCustom) : undefined;
    const allTabs = [
        {
            key: labels.OVERVIEW,
            mode: modes.OVERVIEW,
            covered: hasOverviewCoverage,
            label: labels.OVERVIEW,
            to: toUrl(customMode ? customOverviewTo : buildRoute()),
        },
        {
            key: modes.SEARCH,
            mode: modes.SEARCH,
            covered: hasGoogleCoverage,
            label: labels.SEARCH,
            to: toUrl(customMode ? customGoogleTo : buildRoute('google')),
        },
        {
            key: modes.TIKTOK,
            mode: modes.TIKTOK,
            covered: hasTikTokCoverage,
            label: labels.TIKTOK,
            to: toUrl(customMode ? customTikTokTo : buildRoute('tiktok')),
        },
        {
            key: modes.INSTAGRAM,
            mode: modes.INSTAGRAM,
            covered: hasInstagramCoverage,
            label: labels.INSTAGRAM,
            to: toUrl(customMode ? customInstagramTo : buildRoute('instagram')),
        },
    ];

    // A page family that does not exist for a source has no tab at all, which is
    // a different claim from a source that does not cover this market.
    const tabs = allTabs.filter((t) => !unsupportedTabs.includes(t.key));

    // Nothing to render is the only reason to render nothing: coverage decides
    // how a tab looks, never whether the row exists.
    if (!tabs.some((t) => t.to)) return null;

    return (
        <TooltipProvider delayDuration={300}>
            <div css="display: flex; justify-content: center;">
                <TabList>
                    {tabs
                        .filter((t) => t.to)
                        .map((t) => {
                            const intent =
                                mode === t.mode ? 'primary' : 'secondary';
                            const label = t.beta ? (
                                <Stack $space=".25rem" $direction="row">
                                    <span>{t.label}</span>
                                    <TabButtonBetaBadge intent={intent} />
                                </Stack>
                            ) : (
                                t.label
                            );

                            if (!t.covered)
                                return (
                                    <Tooltip key={t.key}>
                                        <TooltipTrigger asChild>
                                            <TabButtonDisabled intent={intent}>
                                                {label}
                                            </TabButtonDisabled>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {views.dataSourceTabs.notCovered({
                                                source: t.label,
                                                country,
                                            })}
                                        </TooltipContent>
                                    </Tooltip>
                                );

                            return (
                                <TabButtonLink
                                    key={t.key}
                                    intent={intent}
                                    to={t.to}
                                    mixpanelContext={`${t.key}Tab`}
                                >
                                    {label}
                                </TabButtonLink>
                            );
                        })}
                </TabList>
            </div>
        </TooltipProvider>
    );
}

export default DataSourceTabsByCoverage;
